/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { Divider, HStack, Progress, Text } from '@chakra-ui/react'
import React from 'react'
import { FiClock, FiFolder, FiList } from 'react-icons/fi'
import Metadata from '../Metadata'

const CommonData = ({
  itemLabel = 'Batch',
  colorScheme = 'gray',
  currentCount,
  estimatedTime,
  estimatedTimeLabel = 'Estimated time',
  isDone,
  isParallelProcess,
  isProcessing,
  itemIcon = FiFolder,
  itemName,
  processedLabel = 'Successfully processed',
  statusLabel = 'Status',
  statusValue,
  successfulCount,
  totalItems
}) => {
  const percentage = !isParallelProcess
    ? Math.round((currentCount / totalItems) * 100)
    : 0

  const items = []
  const hasItem = successfulCount ?? currentCount ?? totalItems
  if (statusValue) {
    items.push(
      <Metadata
        key="status"
        colorScheme={colorScheme}
        icon={FiList}
        label={statusLabel}
        value={statusValue}
      />
    )
  } else {
    if (itemName) {
      items.push(
        <Metadata
          key="item"
          colorScheme={colorScheme}
          icon={itemIcon}
          label={itemLabel}
          value={itemName}
        />
      )
    }

    if ((hasItem !== undefined) && totalItems && isDone) {
      items.push(
        <Metadata
          key="processed"
          colorScheme={colorScheme}
          icon={FiList}
          label={processedLabel}
          value={`${successfulCount ?? currentCount} / ${totalItems}`}
        />
      )
    }

    if ((hasItem !== undefined) && totalItems && isProcessing) {
      items.push(
        <HStack key="progress" spacing={3} flex="1" minW="150px">
          <Progress
            borderRadius="10"
            colorScheme="blue"
            flex="1"
            isAnimated={true}
            isIndeterminate={isParallelProcess}
            size="sm"
            hasStripe
            value={percentage}
          />
          { !isParallelProcess &&
            <Text fontSize="sm" fontWeight="bold" color="blue.600">
              {successfulCount || currentCount} / {totalItems}
            </Text>
          }
        </HStack>
      )
    }

    if (estimatedTime) {
      items.push(
        <Metadata
          key="estimated-time"
          colorScheme={colorScheme}
          icon={FiClock}
          label={estimatedTimeLabel}
          value={estimatedTime}
        />
      )
    }
  }

  if (!items.length) return null

  return (
    <>
      <HStack
        spacing={5}
        align="center"
        flexWrap="wrap"
      >
        {items.map((item, index) => (
          <React.Fragment key={item.key}>
            { index > 0 &&
              <Divider
                orientation="vertical"
                h={8}
                borderColor={`${colorScheme}.200`}
              />
            }
            { item }
          </React.Fragment>
        ))}
      </HStack>
    </>
  )
}

export default CommonData
